import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Upload } from "lucide-react";
import { api, formatApiError, imgSrc } from "../../lib/api";

const field = "w-full rounded-xl border border-[#EBD08A] bg-white px-4 py-3 outline-none focus:border-[#FF1F8E]";

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/meta").then((r) => { setCats(r.data.categories); setCategory(r.data.categories[0]); });
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a photo first");
    setBusy(true);
    const fd = new FormData();
    fd.append("category", category);
    fd.append("caption", caption);
    fd.append("image", file);
    try {
      await api.post("/admin/gallery", fd);
      toast.success("Photo added to gallery");
      setCaption(""); setFile(null); e.target.reset();
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    await api.delete(`/admin/gallery/${id}`);
    setItems(items.filter((i) => i.id !== id));
    toast.success("Photo removed");
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Gallery manager</h1>
      <form onSubmit={upload} className="mt-6 grid gap-4 rounded-3xl bg-white p-6 ring-1 ring-[#EBD08A] md:grid-cols-[1fr_1fr_1.4fr_auto] md:items-end" data-testid="admin-gallery-form">
        <label><span className="mb-1 block text-sm font-semibold">Photo</span><input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="admin-gallery-file" /></label>
        <label><span className="mb-1 block text-sm font-semibold">Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={field} data-testid="admin-gallery-category">{cats.map((c) => <option key={c}>{c}</option>)}</select>
        </label>
        <label><span className="mb-1 block text-sm font-semibold">Caption</span><input value={caption} onChange={(e) => setCaption(e.target.value)} className={field} placeholder="e.g. Class of 2026 stoles" data-testid="admin-gallery-caption" /></label>
        <button disabled={busy} className="btn-pink !py-3 disabled:opacity-60" data-testid="admin-gallery-upload-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload</button>
      </form>
      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="admin-gallery-grid">
        {items.map((it) => (
          <div key={it.id} className="overflow-hidden rounded-2xl bg-white ring-1 ring-[#EBD08A]" data-testid={`admin-gallery-item-${it.id}`}>
            <img src={imgSrc(it)} alt={it.caption} className="aspect-square w-full object-cover" />
            <div className="flex items-start justify-between gap-2 p-3">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#D4A537]">{it.category}</p>
                <p className="truncate text-sm">{it.caption || "—"}</p>
              </div>
              <button onClick={() => remove(it.id)} className="rounded-full p-2 text-black/50 hover:bg-red-50 hover:text-red-600" data-testid={`admin-gallery-delete-${it.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
